import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Button } from "~/components/2_molecules/button";
import { Input } from "~/components/2_molecules/input";
import { Map as MapComponent } from "~/components/3_organisms/Map";
import { ShowcaseItem } from "~/components/3_organisms/ShowcaseItem";
import { Hero } from "~/components/4_modules/Hero";
import { SEO } from "~/components/SEO";
import { contactApi } from "~/lib/contact-api";
import { localBusinessSchema } from "~/lib/structured-data";
import useAnalytics from "~/hooks/useAnalytics";

export const Route = createFileRoute("/contact")({
  component: Contact,
});

// Helper function to assess lead quality for analytics
function getLeadQuality(formData: any): 'high' | 'medium' | 'low' {
  let score = 0;

  // Phone number provided (high intent)
  if (formData.phone) score += 3;

  // Message length indicates engagement
  if (formData.message.length > 100) score += 2;
  else if (formData.message.length > 50) score += 1;

  // Subject indicates specific interest
  if (formData.subject && formData.subject !== '') score += 1;

  // Complete name indicates serious inquiry
  if (formData.firstName && formData.lastName) score += 1;

  if (score >= 5) return 'high';
  if (score >= 3) return 'medium';
  return 'low';
}

function Contact() {
  const analytics = useAnalytics();
  const formStartTime = useRef<number | null>(null);
  const formInteractionTracked = useRef(false);

  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      privacy: false,
    },
    onSubmit: async ({ value }) => {
      const submissionStartTime = Date.now();

      // Track form submission attempt
      analytics.trackContactSubmission({
        form_completion_time: formStartTime.current ? submissionStartTime - formStartTime.current : 0,
        fields_filled: Object.values(value).filter(v => v && v !== false).length,
        subject_category: value.subject,
        has_phone: !!value.phone,
        message_length: value.message.length
      });

      try {
        const result = await contactApi.submitContactForm(value);

        if (result.success) {
          // Track successful submission
          analytics.trackBusinessEvent('contact_form_submission', {
            success: true,
            submission_time: Date.now() - submissionStartTime,
            lead_quality: getLeadQuality(value),
            contact_method: 'form'
          });

          // Reset form on successful submission
          form.reset();
          formStartTime.current = null;
          formInteractionTracked.current = false;
          alert(result.message);
        } else {
          // Track failed submission
          analytics.trackEvent({
            category: 'technical_insights',
            action: 'form_submission_failed',
            label: 'server_error',
            customParameters: { error_message: result.message }
          });

          alert(
            result.message ||
              "There was an error sending your message. Please try again.",
          );
        }
      } catch (error) {
        // Track submission error
        analytics.trackEvent({
          category: 'technical_insights',
          action: 'form_submission_error',
          label: 'network_error',
          customParameters: { error: error instanceof Error ? error.message : 'Unknown error' }
        });

        console.error("Contact form submission error:", error);
        alert("There was an error sending your message. Please try again.");
      }
    },
  });

  // Track form interaction start
  const handleFormInteraction = () => {
    if (!formInteractionTracked.current) {
      formStartTime.current = Date.now();
      formInteractionTracked.current = true;

      analytics.trackEvent({
        category: 'user_engagement',
        action: 'contact_form_started',
        label: 'form_interaction'
      });
    }
  };

  // Track page view with contact intent
  useEffect(() => {
    analytics.trackUserJourney('contact_page_view', {
      page_section: 'contact',
      intent: 'contact_business'
    });
  }, [analytics]);

  return (
    <>
      <SEO
        title="Contatti"
        socialTitle="Ristorante Tennis & Gourmet Morbegno | Contatti"
        description="Contatta Tennis Gourmet a Morbegno. Via Gregorini 89, telefono 0342 330685. Prenota il tuo tavolo o i campi sportivi nel nostro ristorante e centro sportivo in Valtellina."
        keywords="contatti Tennis Gourmet, prenotazioni Morbegno, telefono ristorante, Via Gregorini, ristorante Morbegno contatti"
        url="https://www.ristorantetennisgourmet.it/contact"
        image="/images/contact-hero.webp"
        structuredData={localBusinessSchema}
      />
      {/* Hero Section */}
      <Hero backgroundImage="/images/contact-hero.webp">
        <h2 className="tg-h1 font-medium uppercase">Contatti</h2>
      </Hero>
      {/* Map Section */}
      <ShowcaseItem
        title="Vieni a trovarci"
        description={
          <>
            <p className="tg-body">
              Via Gregorini 89,<br></br> 23017 Morbegno (SO)
            </p>
            <p className="tg-primary mt-6 xl:mt-8">Contattaci</p>
            <p className="tg-body mt-4 xl:mt-6">
              Telefono:{" "}
              <a href="tel:0342330685" className="hover:text-tennis-400 underline">
                0342 330685
              </a>
            </p>
            <p className="tg-body mt-4 xl:mt-6">
              Mail:{" "}
              <a
                href="mailto:info@ristorantetennisgourmet.it"
                className="hover:text-tennis-400 underline"
              >
                info@ristorantetennisgourmet.it
              </a>
            </p>
          </>
        }
        customContent={true}
      >
        <MapComponent className="order-last aspect-[16/9] rounded-2xl xl:aspect-square xl:rounded-4xl" />
        <img
          src="/images/decor/decor-big.webp"
          alt="page decoration"
          className="absolute top-full -right-[45vw] h-[65vh] w-auto object-cover object-left xl:hidden"
        />
      </ShowcaseItem>
      {/* Hours Section */}
      <ShowcaseItem
        title="Orari di apertura"
        description={
          <>
            <p className="tg-primary mt-6 uppercase xl:mt-12">Bar</p>
            <p className="tg-body mt-2 xl:mt-4">
              Da <span className="font-bold">martedì</span> a{" "}
              <span className="font-bold">domenica</span>
            </p>
            <p className="tg-body mt-2 xl:mt-4">09:00 - 23:00</p>
            <p className="tg-primary mt-6 uppercase xl:mt-12">Cucina</p>
            <p className="tg-body mt-4 font-bold xl:mt-6">Lunedì</p>
            <p className="tg-body mt-2 xl:mt-4">12:00 – 14:30</p>
            <p className="tg-body mt-2 xl:mt-6">
              Da <span className="font-bold">martedì</span> a{" "}
              <span className="font-bold">domenica</span>
            </p>
            <p className="tg-body mt-2 xl:mt-4">12:00 – 14:30</p>
            <p className="tg-body mt-2 xl:mt-4">19:00 – 22:00</p>
          </>
        }
        imagePosition="right"
        image={{
          src: "/images/contact-01.webp",
          alt: "Image of a drink",
          alignment: "bottom",
          mobileHide: true,
        }}
        backgroundStyle="pattern"
        decorPattern={
          <img
            src="/images/decor/decor-big.webp"
            alt="page decoration"
            className="absolute -right-60 bottom-1/2 hidden h-full w-auto object-cover xl:block"
          />
        }
      />
      {/* Contact Section */}
      <section id="contact-form" className="tg-section__wrapper py-12 lg:py-25">
        <img
          src="/images/form-bg.webp"
          alt="Image background for contact form"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="tg-section__grid">
          {/* Contact Form */}
          <h2 className="tg-h2 col-span-12">Hai bisogno di noi? Contattaci</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="col-span-12 grid grid-cols-subgrid space-y-4 lg:space-y-6"
          >
            <form.Field
              name="firstName"
              validators={{
                onChange: ({ value }) => (!value ? "First name is required" : undefined),
              }}
            >
              {(field) => (
                <div className="col-span-6 xl:col-span-4">
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onFocus={handleFormInteraction}
                    placeholder="Nome..."
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      handleFormInteraction();
                    }}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-sm text-red-500">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
            <form.Field
              name="lastName"
              validators={{
                onChange: ({ value }) => (!value ? "Last name is required" : undefined),
              }}
            >
              {(field) => (
                <div className="col-span-6 xl:col-span-4">
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onFocus={handleFormInteraction}
                    placeholder="Cognome..."
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      handleFormInteraction();
                    }}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-sm text-red-500">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return "Email is required";
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return "Please enter a valid email address";
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <div className="col-span-12 xl:col-span-5">
                  <Input
                    id={field.name}
                    name={field.name}
                    type="email"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    placeholder="Email..."
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="mt-1 text-sm text-red-500">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="phone">
              {(field) => (
                <div className="col-span-12 xl:col-span-5">
                  <Input
                    id={field.name}
                    name={field.name}
                    type="tel"
                    placeholder="Telefono..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <div className="col-span-12 grid grid-cols-subgrid items-end space-y-4 lg:space-y-0">
              <div className="col-span-12 xl:col-span-10">
                <form.Field
                  name="message"
                  validators={{
                    onChange: ({ value }) => (!value ? "Message is required" : undefined),
                  }}
                >
                  {(field) => (
                    <div className="w-full">
                      <textarea
                        id={field.name}
                        name={field.name}
                        rows={6}
                        className="tg-body bg-tennis-900/60 ring-offset-background w-full rounded-md px-3 py-2 placeholder:text-white/80 focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Messaggio..."
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="mt-1 text-sm text-red-500">
                          {field.state.meta.errors[0]}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Field name="privacy">
                  {(field) => (
                    <div className="">
                      <label className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          checked={field.state.value}
                          onChange={(e) => field.handleChange(e.target.checked)}
                          className="mt-1"
                          required
                        />
                        <span className="tg-body text-sm">
                          Accetto la{" "}
                          <a href="/privacy_policy" className="underline">
                            Privacy Policy
                          </a>
                        </span>
                      </label>
                    </div>
                  )}
                </form.Field>
              </div>

              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button
                    variant="default"
                    type="submit"
                    size="lg"
                    className="col-span-4 xl:col-span-2 xl:mb-6"
                    disabled={!canSubmit}
                  >
                    {isSubmitting ? "Invio..." : "Invia"}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
